import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, RotateCcw, X } from 'lucide-react';
import { errorText, plural, postBulkUndo, SKIP_LABEL, UNDO_SECONDS, type BulkReassignResult } from './bulkReassign';

/** "10 leads reassigned to X · 2 skipped" + See skipped + Undo for 10 s. */
export function BulkReassignResultBar({ result, onUndone, onClose }: {
  result: BulkReassignResult;
  onUndone: (undo: BulkReassignResult) => void;
  onClose: () => void;
}) {
  const [left, setLeft] = useState(UNDO_SECONDS);
  const [showSkipped, setShowSkipped] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [note, setNote] = useState('');
  const skipped = result.results.filter((r) => r.status === 'skipped');

  useEffect(() => {
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  async function undo() {
    setUndoing(true);
    try {
      const r = await postBulkUndo(result.requestId);
      setNote(r.skipped > 0 ? `Undone for ${plural(r.ok, 'lead')}. ${plural(r.skipped, 'lead')} changed meanwhile and stayed.` : `Undone for ${plural(r.ok, 'lead')}.`);
      setLeft(0);
      onUndone(r);
    } catch (e) {
      setNote(`Undo failed: ${errorText(e)}`);
    } finally {
      setUndoing(false);
    }
  }

  return (
    <div role="status" data-tour="leads-bulk-result" className="mb-3 rounded-xl border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <CheckCircle2 size={16} className="shrink-0 text-green-400" />
        <span className="min-w-0 font-medium text-[var(--text-primary)]">
          {note || `${plural(result.ok, 'lead')} reassigned to ${result.toUser.name}${skipped.length ? ` · ${skipped.length} skipped` : ''}`}
        </span>
        {left > 0 && !note && (
          <button
            type="button"
            onClick={undo}
            disabled={undoing}
            className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {undoing ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Undo ({left}s)
          </button>
        )}
        {skipped.length > 0 && (
          <button type="button" onClick={() => setShowSkipped((v) => !v)} className="text-xs font-medium text-indigo-400 underline hover:text-indigo-500">
            {showSkipped ? 'Hide skipped' : 'See skipped'}
          </button>
        )}
        <button type="button" aria-label="Dismiss" onClick={onClose} className="ml-auto rounded p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)]">
          <X size={14} />
        </button>
      </div>
      {showSkipped && (
        <ul aria-label="Skipped leads" className="mt-2 space-y-0.5 text-xs text-[var(--text-secondary)]">
          {skipped.map((s) => (
            <li key={s.leadId}>{s.name || 'Lead'} — {s.reason ? SKIP_LABEL[s.reason] ?? s.reason : 'skipped'}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
